
import { useEffect, useCallback, useReducer, useRef } from "react";
import { Model } from "../models/model";

const getEmptyForm = () => ({
    id: null,
    nombre: '',
    numero: null,
    id_moneda: null,
    id_banco: null,
    id_tipo_cuenta: null,
    estado: null,
    saldo_inicial: 0,
    saldo_actual: 0,
    simbolo: 'S/',
    separacion_miles: ',',
    separacion_decimales: '.'
});

const initialState = {
    accounts: [],
    latest: [],
    totals: {
        saldo_actual_soles: 0,
        saldo_actual_usd: 0,
        ingresos_del_mes: 0,
        egresos_del_mes: 0
    },
    selectedBank: null,
    formData: getEmptyForm(),
    catalogs: {
        bancos: [],
        tiposCuentas: [],
        monedas: [],
    },
    filters: {
        estado: true
    },
    openDialog: false,
    loading: false,
    error: null,
    accountsLoading:true,
};

function reducerActions(state, action) {
    switch (action.type) {
        case "SET_ERROR":
            return {...state,error:action.payload};
        case "SET_ACCOUNTS_LOADING":
            return {...state,accountsLoading:action.payload};
        case "SET_LATEST":
            return {
                ...state,
                latest: action.payload,
            };

        case "SET_ACCOUNTS":
            return {
                ...state,
                accounts: action.payload,
            };

        case "SET_TOTALS":
            return {
                ...state,
                totals: {
                    ...state.totals,
                    ...action.payload
                }
            };

        case "SET_FORM":
            return {
                ...state,
                formData: {
                    ...state.formData,
                    ...action.payload,
                },
            };

        case "RESET_FORM":
            return {
                ...state,
                formData: initialState.formData,
            };

        case "OPEN_EDIT":
            return {
                ...state,

                formData: {
                    ...action.payload
                },

                openDialog: true
            };

        case "OPEN_CREATE":
            return {
                ...state,
                formData: {
                    ...getEmptyForm(),
                    estado: true
                },

                openDialog: true
            };

        case "SET_FILTER":
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [action.payload.name]: action.payload.value
                },
            };

        case "SET_OPEN_DIALOG":
            return {
                ...state,
                openDialog: action.payload
            };

        case "SET_LOADING":
            return {
                ...state,
                loading: action.payload
            };

        case "SET_CATALOGS":
            return {
                ...state,
                catalogs: action.payload,
            };

        default:
            return state;
    }
}

export function useBank({includeLatest = false} = {}) {

    const [state, dispatch] = useReducer(reducerActions, initialState);
    const accountsRequest = useRef(0);
    const { accounts, latest, totals, formData, catalogs, filters, openDialog, loading, error } = state

    const getLatest = useCallback(async () => {
        try {

            const results = await Model.getAll({
                url: `bancos/latest`,
            });

            dispatch({
                type: "SET_LATEST",
                payload: results.lastest
            });

            dispatch({
                type: "SET_TOTALS",
                payload: {
                    ingresos_del_mes: results.ingresos_del_mes,
                    egresos_del_mes: results.egresos_del_mes
                }
            });

        } catch (error) {

            dispatch({
                type: "SET_ERROR",
                payload: error.message,
            });

        }
    }, []);

    const getAccounts = useCallback(async () => {
        const request = ++accountsRequest.current;
        dispatch({type:'SET_ACCOUNTS_LOADING',payload:true});
        try {

            const params = new URLSearchParams(filters).toString();

            const results = await Model.getAll({
                url: `bancos/accounts/?${params}`,
            });
            if (request !== accountsRequest.current) return;
            if (!results?.banks) throw new Error('No se pudieron cargar las cuentas bancarias.');
            dispatch({type:'SET_ERROR',payload:null});

            dispatch({
                type: "SET_ACCOUNTS",
                payload: results.banks
            });

            dispatch({
                type: "SET_TOTALS",
                payload: {
                    saldo_actual_soles: results.saldo_actual_soles,
                    saldo_actual_usd: results.saldo_actual_usd
                }
            });

        } catch (error) {
            if (request !== accountsRequest.current) return;
            dispatch({
                type: "SET_ERROR",
                payload: error.message,
            });
        } finally {
            if (request === accountsRequest.current) dispatch({type:'SET_ACCOUNTS_LOADING',payload:false});
        }
    }, [filters]);

    const loadCatalogs = async () => {
        try {
        const [
            bancos,
            tiposCuentas,
            monedas,
        ] = await Promise.all([
            Model.getAll({ url: "bancos" }),
            Model.getAll({ url: "bancos/accounts_types" }),
            Model.getAll({ url: "bancos/currencies" }),
        ]);
        if (!Array.isArray(bancos?.bancos) || !Array.isArray(tiposCuentas) || !Array.isArray(monedas)) throw new Error('Catálogos no disponibles');

        dispatch({
            type: "SET_CATALOGS",
            payload: {
                bancos: bancos.bancos,
                tiposCuentas,
                monedas
            },
        });
        } catch (error) {
            dispatch({type:'SET_ERROR',payload:'No se pudieron cargar los catálogos de cuentas.'});
        }
    };

    const setOpenDialog = (open) => {
        dispatch({
            type: "SET_OPEN_DIALOG",
            payload: open
        });
    };

    const openEdit = (account) => {
        dispatch({
            type: "OPEN_EDIT",
            payload: account,
        });
    };

    const openCreate = () => {
        dispatch({
            type: "OPEN_CREATE",
        });
    };

    const handleUpdateForm = (payload) => {
        dispatch({
            type: "SET_FORM",
            payload,
        });
    };

    /*  const handleEditById = (id) => {
         const accountFound = accounts.find((it) => it.id === id);
         if (accountFound) {
             handleUpdateForm(accountFound)
             setOpenDialog(true)
         }
     }; */

    const handleFilters = ({ target }) => {
        dispatch({
            type: "SET_FILTER",
            payload: {
                name: target.name,
                value: target.value,
            },
        });
    };

    const saveForm = async (account = state.formData) => {
        try {

            dispatch({
                type: "SET_LOADING",
                payload: true
            });

            dispatch({type:'SET_ERROR',payload:null});
            if (!account.numero?.trim() || !account.id_moneda || !account.id_banco || !account.id_tipo_cuenta || !Number.isFinite(Number(account.saldo_inicial))) {
                throw new Error('Completa banco, tipo de cuenta, número, moneda y un saldo inicial válido.');
            }

            let result;

            if (account.id) {

                result = await Model.update({
                    url: `bancos/${account.id}`,
                    dataForm: account,
                    resultData: true,
                });

            } else {

                result = await Model.save({
                    url: "bancos",
                    dataForm: account,
                    resultData: true,
                });

            }

            if (!result) throw new Error('No se pudo guardar la cuenta. Revisa los datos e inténtalo de nuevo.');
            setOpenDialog(false);

            dispatch({
                type: "SET_LOADING",
                payload: false
            });


            await getAccounts();

        } catch (error) {
            dispatch({type:'SET_ERROR',payload:error.message});
        } finally {
            dispatch({type:'SET_LOADING',payload:false});
        }
    };

    useEffect(() => {
        loadCatalogs();
    }, []);

    useEffect(() => {
        if (includeLatest) getLatest();
    }, [getLatest,includeLatest]);

    useEffect(() => {
        getAccounts();
    }, [filters, getAccounts]);

    return {
        latest,
        accounts,
        totals,
        error,
        accountsLoading:state.accountsLoading,
        refresh:getAccounts,

        loading,
        openDialog,
        setOpenDialog,

        handleFilters,
        filters,

        formData,
        catalogs,

        openCreate,
        openEdit,
        /* handleEditById, */

        handleUpdateForm,
        saveForm
    };
}
